import { useState, useEffect, useCallback } from 'react'
import { api } from '../services/api/client'
import { createLogger } from '../utils/logger'

const logger = createLogger('useOpenNowFilter')
const REFRESH_INTERVAL = 5 * 60 * 1000 // 5 minutes

/**
 * Fetches the set of entity IDs that are open right now (schedule/events match
 * current day + time). Refreshes every 5 minutes.
 *
 * @param {boolean} enabled - Set false to skip fetching (e.g. entity detail page has no open-now toggle)
 * @returns {{ openNowIds: Set<string>, openNowActive: boolean, toggleOpenNow: () => void }}
 */
export function useOpenNowFilter(enabled = true) {
  const [openNowIds, setOpenNowIds] = useState(new Set())
  const [openNowActive, setOpenNowActive] = useState(false)

  const fetchOpenNow = useCallback(async () => {
    try {
      const res = await api.get('/entities/open-now')
      const ids = res?.data?.data?.entityIds || res?.data?.entityIds || []
      setOpenNowIds(new Set(ids))
    } catch (err) {
      logger.warn('Failed to fetch open-now entities', err)
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    fetchOpenNow()
    const timer = setInterval(fetchOpenNow, REFRESH_INTERVAL)
    return () => clearInterval(timer)
  }, [fetchOpenNow, enabled])

  const toggleOpenNow = useCallback(() => {
    setOpenNowActive(prev => !prev)
  }, [])

  return { openNowIds, openNowActive, toggleOpenNow }
}
